/**
 * Country selector: the list of countries is not part of the page HTML.
 * It is fetched once (Section Rendering API, sections/country-list.liquid) when the shopper is about to open
 * the selector, and the same response is used for the desktop popover and the mobile drawer.
 *
 * Markup contract:
 *   [data-country-list]            element that receives the options (x-listbox on desktop, div in the drawer)
 *   data-country-list-section      file name of the section to request (on the same element)
 *   the element lives inside an <x-popover id="…"> whose toggle button has aria-controls="…"
 */
(function () {
  if (window.__countryListReady) return;
  window.__countryListReady = true;

  var request = null;

  function load(sectionName) {
    if (request) return request;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root + '?' + ['section_id', sectionName].join('=');

    request = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Country list: HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var holder = document.createElement('template');
        holder.innerHTML = html;
        var options = Array.prototype.slice.call(holder.content.querySelectorAll('button[name="country_code"]'));
        if (!options.length) throw new Error('Country list: empty response');
        return sortOptions(options);
      })
      .catch(function (error) {
        request = null; // allow another attempt on the next interaction
        throw error;
      });

    return request;
  }

  // Pinned countries keep the order they come in; the others are ordered with the rules of the page language,
  // so that names starting with Č, Š, Ä, Ö… sit where a shopper expects them instead of at the very end.
  function sortOptions(options) {
    var pinned = options.filter(function (option) { return option.hasAttribute('data-priority'); });
    var others = options.filter(function (option) { return !option.hasAttribute('data-priority'); });

    try {
      var collator = new Intl.Collator(document.documentElement.lang || undefined, { sensitivity: 'base' });
      others.sort(function (a, b) { return collator.compare(a.textContent.trim(), b.textContent.trim()); });
    } catch (e) { /* keep the server order */ }

    return pinned.concat(others);
  }

  function isOpen(popover) {
    return !!popover && (popover.hasAttribute('open') || popover.open === true);
  }

  function applySearch(list) {
    var form = list.closest('form');
    var input = form && form.querySelector('input[type="text"]');
    if (input && input.value) input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // Mirrors the theme's popover: with a mouse or keyboard the selected country is focused and scrolled into view,
  // on touch devices the list simply starts at the top.
  function revealSelected(list, popover) {
    var selected = list.querySelector('[aria-selected="true"]');
    if (!selected) return;

    if (list.tagName.toLowerCase() === 'x-listbox') {
      list.setAttribute('aria-activedescendant', selected.id);
    }
    if (!isOpen(popover) || !window.matchMedia('screen and (pointer: fine)').matches) return;

    var form = list.closest('form');
    var input = form && form.querySelector('input[type="text"]');
    if (input && document.activeElement === input && input.value !== '') return; // the shopper is already searching

    var scroller = list;
    var offset = selected.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    scroller.scrollTop += offset - (scroller.clientHeight - selected.offsetHeight) / 2;

    try { selected.focus({ preventScroll: true }); } catch (e) { selected.focus(); }
  }

  function hydrate(list, popover) {
    if (list.dataset.countryList === 'ready' || list.dataset.countryList === 'loading') return;

    list.dataset.countryList = 'loading';
    list.setAttribute('aria-busy', 'true');

    load(list.getAttribute('data-country-list-section') || 'country-list')
      .then(function (options) {
        var fragment = document.createDocumentFragment();
        Array.prototype.forEach.call(options, function (option, index) {
          var clone = option.cloneNode(true);
          clone.id = (popover && popover.id ? popover.id : 'country') + '-option-' + index;
          fragment.appendChild(clone);
        });

        list.appendChild(fragment);
        list.dataset.countryList = 'ready';
        list.removeAttribute('aria-busy');

        applySearch(list);
        revealSelected(list, popover);
      })
      .catch(function () {
        delete list.dataset.countryList;
        list.removeAttribute('aria-busy');
      });
  }

  function bind(list) {
    if (list.dataset.countryListBound) return;
    list.dataset.countryListBound = '1';

    var popover = list.closest('x-popover');
    var toggle = popover && popover.id ? document.querySelector('[aria-controls="' + popover.id + '"]') : null;
    var start = function () { hydrate(list, popover); };

    if (toggle) {
      // These fire before the popover opens, so the list is usually there by the time it is visible.
      ['pointerenter', 'pointerdown', 'touchstart', 'focus', 'click'].forEach(function (type) {
        toggle.addEventListener(type, start, { passive: true });
      });
      toggle.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar' || event.key === 'ArrowDown') start();
      });
    }

    if (popover) {
      popover.addEventListener('toggle', start);
      popover.addEventListener('dialog:after-show', function () {
        start();
        if (list.dataset.countryList === 'ready') revealSelected(list, popover);
      });
    }
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-country-list]'), bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The header can be re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
